import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { API, StarScore, fmtDate } from './stars';

const thisMonth = () => new Date().toISOString().slice(0, 7);
const HEADS = ['İSG Uzmanı', 'Denetim', 'Verdiği Ortalama Puan', 'Firma', 'Zamanında', 'Geç Yapılan', 'Kaçırılan', 'Zamanında Oranı', 'Son Denetim'];
const rateTone = (v) => v == null ? 'bg-slate-100 text-slate-400' : v >= 90 ? 'bg-green-100 text-green-700' : v >= 70 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-700';

const Detail = ({ r }) => (
  <div className="grid gap-4 bg-slate-50 p-3 text-sm md:grid-cols-2" data-testid={`auditor-detail-${r.id}`}>
    <div>
      <p className="mb-1 font-semibold text-orange-700">Geç Yapılan Denetimler ({r.late})</p>
      {r.late_items.length === 0 ? <p className="text-xs text-slate-400">Yok</p> : r.late_items.map((x, i) => (
        <p key={i} className="text-xs"><b className="font-mono">{x.request_number || '-'}</b> · {x.company_name} · Son gün {fmtDate(x.deadline)} → Denetim {fmtDate(x.audited)} <span className="ml-1 rounded bg-orange-100 px-1.5 font-bold text-orange-700">+{x.late_days} gün</span>{x.off_program && <span className="ml-1 rounded bg-slate-200 px-1.5 text-[10px]">Program dışı</span>}</p>
      ))}
    </div>
    <div>
      <p className="mb-1 font-semibold text-red-700">Kaçırılan Denetimler ({r.missed})</p>
      {r.missed_items.length === 0 ? <p className="text-xs text-slate-400">Yok</p> : r.missed_items.map((x, i) => (
        <p key={i} className="text-xs"><b className="font-mono">{x.request_number || '-'}</b> · {x.company_name} · Son gün {fmtDate(x.deadline)} · Talep bitişi {fmtDate(x.end)} <span className="ml-1 rounded bg-red-600 px-1.5 font-bold text-white">Denetim Yapılmadı</span>{x.off_program && <span className="ml-1 rounded bg-slate-200 px-1.5 text-[10px]">Program dışı</span>}</p>
      ))}
    </div>
  </div>
);

const AuditorRow = ({ r, open, onToggle }) => (
  <>
    <TableRow className={`cursor-pointer ${r.audits || r.missed ? '' : 'text-slate-400'}`} onClick={onToggle} data-testid={`auditor-row-${r.id}`}>
      <TableCell className="font-medium">{r.name}</TableCell>
      <TableCell><span className={`rounded px-2 py-0.5 text-sm font-bold ${r.audits ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-400'}`} data-testid={`auditor-count-${r.id}`}>{r.audits}</span></TableCell>
      <TableCell>{r.avg != null ? <StarScore value={r.avg} /> : <span className="text-xs">-</span>}</TableCell>
      <TableCell>{r.companies}</TableCell>
      <TableCell className="font-semibold text-green-700" data-testid={`auditor-ontime-${r.id}`}>{r.on_time}</TableCell>
      <TableCell data-testid={`auditor-late-${r.id}`}>{r.late ? <span className="text-orange-700"><b>{r.late}</b> <span className="text-xs">(toplam +{r.late_days} gün, ort. {r.avg_delay})</span></span> : 0}</TableCell>
      <TableCell className={r.missed ? 'font-bold text-red-600' : ''} data-testid={`auditor-missed-${r.id}`}>{r.missed}</TableCell>
      <TableCell><span className={`rounded px-2 py-0.5 text-sm font-bold ${rateTone(r.on_time_rate)}`} data-testid={`auditor-rate-${r.id}`}>{r.on_time_rate == null ? '-' : `%${r.on_time_rate}`}</span></TableCell>
      <TableCell className="text-sm">{r.last_audit_at ? fmtDate(r.last_audit_at) : '-'}</TableCell>
    </TableRow>
    {open && <TableRow><TableCell colSpan={HEADS.length} className="p-0"><Detail r={r} /></TableCell></TableRow>}
  </>
);

// Her İSG uzmanının seçilen aydaki denetim sayısı ve verdiği ortalama puan
export const AuditorPerformance = () => {
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    if (!month) return;
    setRows(null);
    axios.get(`${API}/five-star/auditor-stats`, { params: { month } }).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [month]);
  const pdf = async () => {
    try {
      const r = await axios.get(`${API}/five-star/auditor-stats/pdf`, { params: { month }, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `uzman_performansi_${month}.pdf`; a.click();
    } catch { toast.error('PDF oluşturulamadı'); }
  };
  const total = (rows || []).reduce((t, r) => t + r.audits, 0);
  return (
    <div className="space-y-4 rounded-lg border bg-white p-4" data-testid="auditor-performance">
      <div className="flex flex-wrap items-center gap-3">
        <UserCheck className="h-5 w-5 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800">Ay</span>
        <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="auditor-month-input" />
        <span className="text-sm text-slate-500" data-testid="auditor-total">Toplam {total} denetim</span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={pdf} disabled={!month} data-testid="auditor-pdf-btn"><Download className="mr-1 h-4 w-4" />PDF İndir</Button>
      </div>
      <p className="text-xs text-slate-500">Denetim, talep süresi içinde en geç son denetim gününde yapılmalıdır. Talebin ilk denetimi esas alınır; son günden sonra yapılan her gün gecikme sayılır. Sorumluluk Ayarlar {'>'} İSG Uzmanı Çalışma Programı'na göre belirlenir; programa uymayan talepler ("Program dışı") lokasyondan sorumlu tüm İSG uzmanlarına yazılır. Biri zamanında denetlerse sorumlulardan kimse ceza almaz; hiç denetlenmeden biten talep tüm sorumlulara "Kaçırılan" yazılır. Satıra tıklayarak ayrıntıyı görün.</p>
      <Table>
        <TableHeader><TableRow className="bg-slate-50">{HEADS.map(h => <TableHead key={h} className="font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows === null ? <TableRow><TableCell colSpan={HEADS.length} className="py-8 text-center text-slate-400">Yükleniyor...</TableCell></TableRow>
            : rows.length === 0 ? <TableRow><TableCell colSpan={HEADS.length} className="py-8 text-center text-slate-400">İSG uzmanı yok</TableCell></TableRow>
            : rows.map(r => <AuditorRow key={r.id} r={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />)}
        </TableBody>
      </Table>
    </div>
  );
};
